import React, { useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

import { useTranslation } from '@lyearn/i18n';
import { AlertTriangleOutline, CheckmarkOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Input, Loader, Stack, Typography } from '@lyearn/ui';
import { FormFieldContainer } from '@/components/Form';

import useSearchJira from '../../hooks/useSearchJira';

interface JQLQueryProps {
  value?: string | null;
  onChange: (val: string) => void;
  onSetQueryValid?: (isValid: boolean) => void;
  error?: string;
  as?: 'form-field' | 'input';
}

type Info = {
  validation: 'success' | 'error';
  text?: string;
};

const DEBOUNCE_DURATION = 500;

const renderInfoComponent = (as: JQLQueryProps['as'], info?: Info) => {
  if (as === 'input' && info?.validation === 'success') {
    return (
      <Stack className="px-24">
        <Stack
          alignItems="center"
          className={cn('mt-12 h-48 w-full rounded-8 px-16', {
            'bg-bg-secondary': info?.text,
          })}>
          <Typography variant="body-short-01">{info?.text}</Typography>
        </Stack>
      </Stack>
    );
  }

  if (info?.validation === 'success' && info.text) {
    return (
      <Stack alignItems="center" className="mt-20 h-46 w-full rounded-8 bg-bg-secondary px-16">
        <Typography variant="body-short-02">{info.text}</Typography>
      </Stack>
    );
  }

  return null;
};

const renderEndAdorment = (as: JQLQueryProps['as'], isFetching: boolean, info?: Info) => {
  if (isFetching) {
    return <Loader className={as === 'input' ? 'w-24' : undefined} />;
  }

  if (info?.validation === 'error') {
    return (
      <AlertTriangleOutline
        className={as === 'input' ? 'h-24 w-24' : 'h-20 w-20'}
        fillColorToken="icon-error"
      />
    );
  }

  if (info?.validation === 'success') {
    return (
      <CheckmarkOutline
        className={as === 'input' ? 'h-24 w-24' : 'h-20 w-20'}
        fillColorToken="icon-success"
      />
    );
  }
};

function JQLQuery(props: JQLQueryProps) {
  const { value, onChange, error, as = 'input', onSetQueryValid } = props;
  const { t } = useTranslation('Jira');
  const [inputVal, setInputVal] = useState(value || '');
  const [info, setInfo] = useState<Info>();
  const [isFetching, setIsFetching] = useState(false);
  const { searchJira } = useSearchJira();

  const validateJQL = useCallback(
    async (text: any) => {
      setIsFetching(true);
      const data = await searchJira(text);
      setIsFetching(false);
      if (!data.success) {
        setInfo({
          validation: 'error',
          text: t('Jira:JQLQueryInvalid'),
        });
        onSetQueryValid?.(false);

        return;
      }

      setInfo({
        validation: 'success',
        text: t('Jira:IssuesFoundInJira', {
          issueCount: data.jiraIssues?.totalCount,
        }),
      });
      onSetQueryValid?.(true);
    },
    [onSetQueryValid, searchJira, t],
  );

  const debounceOnChange = useMemo(
    () =>
      debounce((text) => {
        onChange(text);
        if (text) {
          validateJQL(text);
        }
      }, DEBOUNCE_DURATION),
    [onChange, validateJQL],
  );

  const _onChange = useCallback(
    (val: any) => {
      setInputVal(val);
      debounceOnChange(val);
      setInfo(undefined);
    },
    [debounceOnChange],
  );

  const InputComp = (
    <Input
      endAdornment={renderEndAdorment(as, isFetching, info)}
      error={info?.validation === 'error' || Boolean(error)}
      placeholder={t('Jira:InsertJQLQuery')}
      size={as === 'input' ? 'large' : undefined}
      value={inputVal}
      onChange={(e) => _onChange(e.target.value)}
    />
  );

  const formError = useMemo(() => {
    const errorStr = info?.validation === 'error' && info?.text ? info.text : undefined;
    if (as === 'input') {
      return errorStr || error;
    }

    return errorStr;
  }, [as, error, info?.text, info?.validation]);

  return (
    <>
      <FormFieldContainer
        className={cn('w-full', {
          'px-24': as === 'input',
        })}
        error={formError}
        label={as === 'form-field' ? t('Jira:JQLQuery') : ''}>
        {InputComp}
      </FormFieldContainer>
      {renderInfoComponent(as, info)}
    </>
  );
}

export default JQLQuery;
